import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
const MODULE_KEY = "outreach_trackers";
const authHeader = (token) => ({ Authorization: `Bearer ${token}`, "x-module-key": MODULE_KEY });
const buildQuery = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "")
            qs.set(k, String(v));
    });
    const str = qs.toString();
    return str ? `?${str}` : "";
};
const EMPTY_FILTERS = {
    clients: [],
    account_managers: [],
    os_users: [],
    statuses: [],
    types: [],
};
const initialState = {
    rows: [],
    pagination: { total: 0, page: 1, limit: 10, total_pages: 1 },
    loading: false,
    error: null,
    filters: EMPTY_FILTERS,
    filtersLoaded: false,
    saving: false,
};
// Fetch EVERY row matching the current tab/filters (across all pages) for export.
// The list endpoint caps `limit` at 100, so we page through until done. This does NOT
// write to the slice state — it returns the full set for the caller to export.
export const fetchOutreachListAll = createAsyncThunk("outreachTracker/fetchListAll", async (query, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const LIMIT = 100;
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
            const res = await ApiCall("GET", `/api/outreach-tracker/list${buildQuery({ ...query, page, limit: LIMIT })}`, undefined, authHeader(token));
            const data = res?.data;
            if (!data?.success)
                return rejectWithValue(data?.message || "Failed to load data for export");
            all.push(...(data.data || []));
            totalPages = data.pagination?.total_pages || 1;
            page += 1;
        } while (page <= totalPages);
        return all;
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchOutreachFilters = createAsyncThunk("outreachTracker/fetchFilters", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("GET", "/api/outreach-tracker/filters", undefined, authHeader(token));
        const data = res?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to load filters");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchOutreachList = createAsyncThunk("outreachTracker/fetchList", async (query, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("GET", `/api/outreach-tracker/list${buildQuery(query)}`, undefined, authHeader(token));
        const data = res?.data;
        if (data?.success) {
            return {
                rows: data.data || [],
                pagination: data.pagination || { total: 0, page: query.page, limit: query.limit, total_pages: 1 },
            };
        }
        return rejectWithValue(data?.message || "Failed to load list");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchOutreachClientMeta = createAsyncThunk("outreachTracker/fetchClientMeta", async ({ client_id, type }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("GET", `/api/outreach-tracker/client-meta${buildQuery({ client_id, type })}`, undefined, authHeader(token));
        const data = res?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to load client meta");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchOutreachCounts = createAsyncThunk("outreachTracker/fetchCounts", async ({ type, tracker_ref_id }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("GET", `/api/outreach-tracker/tracker-counts${buildQuery({ type, tracker_ref_id })}`, undefined, authHeader(token));
        const data = res?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to load counts");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const fetchOutreachDetails = createAsyncThunk("outreachTracker/fetchDetails", async (id, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("GET", `/api/outreach-tracker/details${buildQuery({ id })}`, undefined, authHeader(token));
        const data = res?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to load details");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const manageOutreachTracker = createAsyncThunk("outreachTracker/manage", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const res = await ApiCall("POST", "/api/outreach-tracker/manage", payload, authHeader(token));
        const data = res?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Operation failed");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
const outreachTrackerSlice = createSlice({
    name: "outreachTracker",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchOutreachFilters.fulfilled, (state, action) => {
            state.filters = action.payload;
            state.filtersLoaded = true;
        })
            .addCase(fetchOutreachList.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchOutreachList.fulfilled, (state, action) => {
            state.loading = false;
            state.rows = action.payload.rows;
            state.pagination = action.payload.pagination;
        })
            .addCase(fetchOutreachList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to load list";
        })
            .addCase(manageOutreachTracker.pending, (state) => {
            state.saving = true;
        })
            .addCase(manageOutreachTracker.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(manageOutreachTracker.rejected, (state) => {
            state.saving = false;
        });
    },
});
export const selectOutreachRows = (state) => state.outreachTracker.rows;
export const selectOutreachPagination = (state) => state.outreachTracker.pagination;
export const selectOutreachLoading = (state) => state.outreachTracker.loading;
export const selectOutreachFilters = (state) => state.outreachTracker.filters;
export const selectOutreachFiltersLoaded = (state) => state.outreachTracker.filtersLoaded;
export const selectOutreachSaving = (state) => state.outreachTracker.saving;
export default outreachTrackerSlice.reducer;
